import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from "@angular/core";
import { Lot } from "../../../lot/models/lot.model";
import { OfCardComponent } from "../../../of/components/of-card/of-card.component";
import { PercentComponent } from "../../../../shared/components/percent/percent.component";
import { TypePercent } from "../../../../shared/enums/enums/typePercent.enum";
import { OF } from "../../../of/models/of.model";
import { Router } from "@angular/router";
import { SharedModule } from "../../../../shared/shared.module";
import { ArticlesInfosFacadeService } from "../../services/article-infos/article-infos-facade.service";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { Widget } from "../../../dashboard/models/dashboard.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { ArticleFacadeService } from "../../services/article/article-facade.service";

// ⬇️ pour bouton filtre dans le header
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { PopoverModule } from "../../../../shared/modules/popover/popover.module";
import { AutoSignificantDigitsPipe } from "../../../../shared/pipes/number-sig.pipe";
import { parseNumber } from "../../../../shared/utils/number-utils.service";

type Row = {
  id: string;
  lot: Lot;
  dateDebut?: Date | string | null;
  dateFin?: Date | string | null;
  qte: number;
  of: OF;

  // Coûts
  matTotal: number;
  matPU: number;
  perTotal: number;
  perPU: number;
  pu: number;

  // Perf
  scoreMat: number;
  scorePer: number;
  scoreTotal: number;

  unite: string;
};

type SortBy = "DATE" | "QTE" | "PU" | "SCORE";
type SortOrder = "DESC" | "ASC";
type PerfFilter = "ALL" | "UP" | "DOWN"; // UP ≥ 100%, DOWN < 100%

@Component({
  selector: "vl-of-productions-table",
  standalone: true,
  imports: [
    CommonModule,
    OfCardComponent,
    PercentComponent,
    SharedModule,
    ChipsToggleComponent,
    WidgetTitleComponent,
    ChipsComponent,
    PopoverModule,
    AutoSignificantDigitsPipe,
  ],
  templateUrl: "./of-productions-table.component.html",
  styleUrls: ["./of-productions-table.component.scss"],
  host: { class: "widget-container" },
})
export class OfProductionsTableComponent {
  metadata = input<EntityMetadata>();
  widget = input<Widget>();
  articleFacade = inject(ArticleFacadeService);
  stats = this.articleFacade.getState().stats;
  /** (optionnel) limite initiale (sert de valeur par défaut au slider Top N) */
  limit = input(12);

  articleInfosFacade = inject(ArticlesInfosFacadeService);
  router = inject(Router);
  goOf = output();

  // ==== Filtres ====
  topCount = signal<number>(12);
  search = signal<string>("");
  sortBy = signal<SortBy>("DATE");
  sortOrder = signal<SortOrder>("DESC");
  perfFilter = signal<PerfFilter>("ALL");

  elementsSort = [
    { name: "Tri date fin", value: "DATE" },
    { name: "Tri quantité", value: "QTE" },
    { name: "Tri PU revient", value: "PU" },
    { name: "Tri score", value: "SCORE" },
  ];
  elementsOrder = [
    { name: "Desc", value: "DESC" },
    { name: "Asc", value: "ASC" },
  ];
  elementsPerf = [
    { name: "Tous", value: "ALL" },
    { name: "≥ 100%", value: "UP" },
    { name: "< 100%", value: "DOWN" },
  ];

  private _initFromLimit = false;

  /** mapping brut */
  private baseRows = computed<Row[]>(() => {
    const ofs = (this.stats()?.ofs ?? []) as any[];
    return ofs.map((of) => {
      const id = of.id as string;
      const lot = of?.lots?.[0];

      const qte = parseNumber(of?.quantiteProduite ?? 0);

      // Totaux matière/personnel: Réel sinon Théorique
      const matTotal =
        parseNumber(
          of?.coutMatiereTotalReel ?? of?.coutMatiereTotalTheorique ?? 0,
        ) || 0;
      const perTotal =
        parseNumber(
          of?.coutPersonnelTotalReel ?? of?.coutPersonnelTotalTheorique ?? 0,
        ) || 0;

      const matPU = qte > 0 ? matTotal / qte : 0;
      const perPU = qte > 0 ? perTotal / qte : 0;

      const scoreMat = parseNumber(of?.scoreMatiere ?? 0) - 100;
      const scorePer = parseNumber(of?.scorePersonnel ?? 0);
      const scoreTotal = parseNumber(of?.scoreTotal ?? 0);

      // Libellé unité : fallback 'u'
      const unite = "u";

      const pu = matPU + perPU;

      return {
        id,
        lot,
        of,
        dateDebut: of?.dateDebut ?? null,
        dateFin: of?.dateFin ?? null,
        qte,
        matTotal,
        matPU,
        perTotal,
        perPU,
        scoreMat,
        scorePer,
        scoreTotal,
        pu,
        unite,
      };
    });
  });

  /** recherche + filtre perf */
  private filtered = computed(() => {
    const q = this.search().trim().toLowerCase();
    const pf = this.perfFilter();
    return this.baseRows().filter((r) => {
      // Recherche sur numéro d’OF, article (si dispo), lot
      const ofNum = String((r.of as any)?.numero ?? "").toLowerCase();
      const lotNum = String((r.lot as any)?.numero ?? "").toLowerCase();
      const articleDenom =
        this.articleInfosFacade
          .getByArticleId((r.of as any)?.articleId)?.()
          ?.denomination?.toLowerCase?.() ?? "";

      const okSearch = q
        ? ofNum.includes(q) || lotNum.includes(q) || articleDenom.includes(q)
        : true;

      const s = parseNumber(r.scoreTotal || 0);
      const okPerf =
        pf === "ALL" ? true : pf === "UP" ? s >= 100 : s > 0 && s < 100;

      return okSearch && okPerf;
    });
  });

  /** tri dynamique */
  private sorted = computed(() => {
    const arr = [...this.filtered()];
    const by = this.sortBy();
    const order = this.sortOrder();

    arr.sort((a, b) => {
      let va = 0,
        vb = 0;
      switch (by) {
        case "QTE":
          va = a.qte;
          vb = b.qte;
          break;
        case "PU":
          va = a.pu;
          vb = b.pu;
          break;
        case "SCORE":
          va = a.scoreTotal || 0;
          vb = b.scoreTotal || 0;
          break;
        case "DATE":
        default:
          va = new Date(a.dateFin ?? 0).getTime();
          vb = new Date(b.dateFin ?? 0).getTime();
          break;
      }
      // DESC par défaut
      return (vb - va) * (order === "DESC" ? 1 : -1);
    });
    return arr;
  });

  /** Top N (slider) */
  maxRows = computed(() => this.filtered().length);
  rowsTop = computed<Row[]>(() => {
    //const n = Math.max(1, Math.min(this.topCount(), this.maxRows()));
    //return this.sorted().slice(0, n);
    return this.sorted();
  });

  protected readonly TypePercent = TypePercent;

  constructor() {
    // Initialiser le slider depuis @Input() limit (une seule fois)
    effect(
      () => {
        const l = parseNumber(this.limit());
        if (!this._initFromLimit && Number.isFinite(l) && l > 0) {
          this.topCount.set(l);
          this._initFromLimit = true;
        }
      },
      { allowSignalWrites: true },
    );

    // Clamp auto du slider si le max varie
    effect(
      () => {
        const max = this.maxRows();
        const cur = this.topCount();
        const clamped = Math.min(Math.max(1, cur), Math.max(1, max));
        if (clamped !== cur) this.topCount.set(clamped);
      },
      { allowSignalWrites: true },
    );
  }

  onGoOF(ofId: string) {
    this.router.navigate([`/productions/suivi/stats`], {
      queryParams: { ofId },
    });
    this.goOf.emit();
  }

  protected readonly Math = Math;
}
